import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { TranslationKey } from "../../../shared/i18n/i18n";
import { disableAdminUser, getAdminSystemInformation } from "../../../shared/requests";
import { AdminSystemInformationDetail } from "../../types/adminEntities";
import { useAdminLoading } from "../shell/AdminLoadingContext";
import { AdminDetailSectionLayout } from "../shell/detail/AdminDetailSectionLayout";
import { AdminTextField } from "../shell/detail/AdminTextField";

type AdminAdministrationPageProps = {
  isSuperAdmin: boolean;
  t: (key: TranslationKey) => string;
};

export const AdminAdministrationPage = ({ isSuperAdmin, t }: AdminAdministrationPageProps) => {
  const { track } = useAdminLoading();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [hasLoadedSystemInformation, setHasLoadedSystemInformation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<null | string>(null);
  const [systemInformation, setSystemInformation] = useState<AdminSystemInformationDetail | null>(null);

  const activeTab = location.pathname.endsWith("/disable-user") ? "disable-user" : "system-information";

  if (!isSuperAdmin) {
    return <Navigate replace to="/a/organizations" />;
  }

  const getAdministrationTabPath = useCallback((tab: "disable-user" | "system-information") => (
    tab === "disable-user"
      ? "/a/administration/disable-user"
      : "/a/administration/system-information"
  ), []);

  const loadSystemInformation = useCallback(async () => {
    setErrorMessage(null);
    try {
      const { payload } = await track(async () => getAdminSystemInformation());
      setSystemInformation(payload);
    } catch (error) {
      const fallbackMessage = t("admin.administration.systemInformation.error");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setSystemInformation(null);
      setErrorMessage(message || fallbackMessage);
    } finally {
      setHasLoadedSystemInformation(true);
    }
  }, [t, track]);

  useEffect(() => {
    if (activeTab !== "system-information" || hasLoadedSystemInformation) {
      return;
    }
    void loadSystemInformation();
  }, [activeTab, hasLoadedSystemInformation, loadSystemInformation]);

  const handleDisableUser = async () => {
    setErrorMessage(null);
    setStatusMessage(null);
    setIsSubmitting(true);
    try {
      const { payload } = await track(async () => disableAdminUser({ email }));
      setEmail("");
      setStatusMessage(payload.success ? t("admin.administration.disableUser.success") : null);
    } catch (error) {
      const fallbackMessage = t("admin.administration.disableUser.error");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTabChange = (nextTab: "disable-user" | "system-information") => {
    if (nextTab === activeTab) {
      return;
    }
    setErrorMessage(null);
    setStatusMessage(null);
    navigate(getAdministrationTabPath(nextTab));
  };

  return (
    <section className="admin-shell-right-pane">
      <AdminDetailSectionLayout
        actions={null}
        errorMessage={errorMessage}
        eyebrow={t("admin.route.administration.title")}
        title={t("admin.administration.title")}
      >
        <div className="admin-profile-tabs" role="tablist" aria-label={t("admin.administration.tabs.title")}>
          <button
            aria-controls="admin-administration-disable-user-panel"
            aria-selected={activeTab === "disable-user"}
            className={`admin-profile-tab${activeTab === "disable-user" ? " is-active" : ""}`}
            id="admin-administration-disable-user-tab"
            onClick={() => handleTabChange("disable-user")}
            role="tab"
            type="button"
          >
            {t("admin.administration.tab.disableUser")}
          </button>
          <button
            aria-controls="admin-administration-system-information-panel"
            aria-selected={activeTab === "system-information"}
            className={`admin-profile-tab${activeTab === "system-information" ? " is-active" : ""}`}
            id="admin-administration-system-information-tab"
            onClick={() => handleTabChange("system-information")}
            role="tab"
            type="button"
          >
            {t("admin.administration.tab.systemInformation")}
          </button>
        </div>
        <section
          aria-labelledby="admin-administration-disable-user-tab"
          className={`admin-profile-panel${activeTab === "disable-user" ? " is-active" : ""}`}
          id="admin-administration-disable-user-panel"
          role="tabpanel"
        >
          <div className="admin-profile-security">
            <section className="admin-profile-security-section">
              <AdminTextField
                isReadOnly={isSubmitting}
                label={t("admin.field.email")}
                onChange={setEmail}
                t={t}
                value={email}
                visibility="not-applicable"
              />
              <div className="admin-profile-security-actions">
                <button
                  className="primary-button"
                  disabled={isSubmitting || email.trim().length === 0}
                  onClick={() => void handleDisableUser()}
                  type="button"
                >
                  {isSubmitting ? t("admin.administration.disableUser.submitting") : t("admin.administration.disableUser.submit")}
                </button>
              </div>
              {statusMessage ? <p className="status-message is-success">{statusMessage}</p> : null}
            </section>
          </div>
        </section>
        <section
          aria-labelledby="admin-administration-system-information-tab"
          className={`admin-profile-panel${activeTab === "system-information" ? " is-active" : ""}`}
          id="admin-administration-system-information-panel"
          role="tabpanel"
        >
          <div className="admin-profile-security">
            <section className="admin-profile-security-section">
              <AdminTextField
                isReadOnly={true}
                label={t("admin.administration.systemInformation.environment")}
                t={t}
                value={systemInformation?.environment ?? ""}
                visibility="not-applicable"
              />
              <AdminTextField
                isReadOnly={true}
                label={t("admin.administration.systemInformation.applicationVersion")}
                t={t}
                value={systemInformation?.applicationVersion ?? ""}
                visibility="not-applicable"
              />
              <AdminTextField
                isReadOnly={true}
                label={t("admin.administration.systemInformation.gitBranch")}
                t={t}
                value={systemInformation?.gitBranch ?? ""}
                visibility="not-applicable"
              />
              <AdminTextField
                isReadOnly={true}
                label={t("admin.administration.systemInformation.gitCommitId")}
                t={t}
                value={systemInformation?.gitCommitId ?? ""}
                visibility="not-applicable"
              />
            </section>
          </div>
        </section>
      </AdminDetailSectionLayout>
    </section>
  );
};
